import type { ProjectAggregation } from "../Performance/Charts/useProjectAggregation";
import { REST_PROJECT_AGGREGATION_TARGET_ID } from "../Performance/Charts/useProjectAggregation";
import type { FailureAggregation } from "../Diagnosis/Charts/useFailureAggregation";
import type { DiagnosisReporting, PerformanceReporting } from "../../../../api/useReporting";
import type { Reparability } from "../../../../api/Reparability";

/**
 * Aggregate values given a reducer function and a predicate function.
 * The predicate function is used to filter values by performance indicator and aggregation target.
 * The reducer function is used to aggregate reporting values for a given period (choosing between measured and expected value).
 * @param values Current reporting values
 * @param methods Methods to aggregate values
 * @param methods.reducerFn Reducer function to aggregate values
 * @param methods.predicateFn Predicate function to filter values
 * @returns Aggregated values
 */
export function aggregateSerieValues<T extends { value: number; expected: number }>(
  values: T[],
  reducerFn: (currentValue: number | null, measured: number, expected: number) => number | null,
  predicateFn: (entry: T) => boolean,
): number | null {
  return values.reduce<number | null>(
    (acc, entry) => (predicateFn(entry) ? reducerFn(acc, entry.value, entry.expected) : acc),
    null,
  );
}

/**
 * Create a predicate function to filter values by performance indicator and aggregation target
 *
 * @param reporting Current reporting
 * @param performanceIndicatorName Current selected performance indicator (QOI)
 * @param aggregation Current selected aggregation
 * @param aggregationTargetId Current selected aggregation target (project, type or site)
 * @returns Predicate function to filter values by performance indicator and aggregation target
 */
export function aggregationByProjectAndPI(
  reporting: PerformanceReporting,
  performanceIndicatorName: string | null,
  aggregation: ProjectAggregation,
  aggregationTargetId: string,
) {
  const isProject = aggregation === "PROJECT";
  const isType = aggregation === "TYPE";
  const isSite = aggregation === "SITE";
  return ({ qoiId, projectId }: PerformanceReporting["periods"][number]["values"][number]) => {
    const matchPI = reporting.qoiById[qoiId]?.name === performanceIndicatorName;
    if (!matchPI) return false;
    const project = reporting.projectById[projectId];
    const typeId = project?.typeId;
    const siteId = project?.siteId;
    return aggregationTargetId === REST_PROJECT_AGGREGATION_TARGET_ID
      ? (isProject && !projectId) || (isType && !typeId) || (isSite && !siteId)
      : (isProject && aggregationTargetId === projectId) ||
          (isType && aggregationTargetId === typeId) ||
          (isSite && aggregationTargetId === siteId);
  };
}

/**
 * Create a predicate function to filter values by performance indicator and aggregation target
 *
 * @param reporting Current reporting
 * @param performanceIndicatorName Current selected performance indicator (QOI)
 * @param aggregation Current selected aggregation
 * @param aggregationTargetId Current selected aggregation target (project, type or site)
 * @returns Predicate function to filter values by performance indicator and aggregation target
 */
export function aggregationByFailureAndPI(
  reporting: DiagnosisReporting,
  performanceIndicatorName: string | null,
  aggregation: FailureAggregation,
  aggregationTargetId: string,
) {
  const isCategory = aggregation === "CATEGORY";
  const isDegradationType = aggregation === "DEGRADATION_TYPE";
  const isFailure = aggregation === "FAILURE";
  const isReparability = aggregation === "REPARABILITY";
  const isSystem = aggregation === "SYSTEM";

  return ({
    qoiId,
    failureId,
    reparability,
  }: {
    qoiId: string;
    failureId: string;
    reparability: Reparability | null;
  }) => {
    if (reporting.qoiById[qoiId]?.name !== performanceIndicatorName) return false;
    const {
      name: failureName,
      systemId,
      degradationTypeId,
      failureCategoryId,
    } = reporting.failureById[failureId] || {};

    return aggregationTargetId === REST_PROJECT_AGGREGATION_TARGET_ID
      ? (isFailure && !failureId) ||
          (isCategory && !failureCategoryId) ||
          (isDegradationType && !degradationTypeId) ||
          (isSystem && !systemId) ||
          (isReparability && !reparability)
      : (isFailure && aggregationTargetId === failureName) ||
          (isCategory && aggregationTargetId === failureCategoryId) ||
          (isDegradationType && aggregationTargetId === degradationTypeId) ||
          (isSystem && aggregationTargetId === systemId) ||
          (isReparability && aggregationTargetId === reparability);
  };
}
